import { generateRule } from './generate'
import {
	buildScopedRules,
	getUtilityScope,
	stripUtilityScopeClasses
} from './scope'

const generated = new Set<string>()
const generatedScopes = new Set<string>()
let styleEl: HTMLStyleElement | null = null
let mutationObserver: MutationObserver | null = null
let initializedDocument: Document | null = null

function getStyleEl(doc?: Document) {
	if (doc && styleEl && styleEl.ownerDocument !== doc) {
		styleEl = null
	}
	if (styleEl) return styleEl
	if (doc == null) return null
	styleEl = doc.createElement('style')
	styleEl.setAttribute('data-yakiniku-utils', '')
	doc.head.appendChild(styleEl)
	return styleEl
}

function resetRuntimeForDocument(doc: Document) {
	if (initializedDocument === doc) return
	initializedDocument = doc
	styleEl = null
	generated.clear()
	generatedScopes.clear()
}

function inject(className: string, css: string) {
	if (generated.has(className)) return
	generated.add(className)
	const el = getStyleEl()
	if (el) el.textContent += css
}

function ensureClass(name: string) {
	if (generated.has(name)) return
	const rule = generateRule(name)
	if (rule) inject(name, rule)
}

function getNormalizedClassString(classString: string) {
	const tokens = stripUtilityScopeClasses(
		classString.split(/\s+/).filter(Boolean)
	)

	for (const cls of tokens) {
		if (cls) ensureClass(cls)
	}

	const scope = getUtilityScope(tokens)
	if (scope == null) {
		return tokens.join(' ')
	}

	if (!generatedScopes.has(scope.scopeClass)) {
		const css = buildScopedRules(scope)
		if (css.length > 0) {
			generatedScopes.add(scope.scopeClass)
			const el = getStyleEl()
			if (el) el.textContent += css
		}
	}

	return [...tokens, scope.scopeClass].join(' ')
}

function normalizeElementClass(el: Element) {
	const currentClass = el.getAttribute('class') ?? ''
	const normalized = getNormalizedClassString(currentClass)
	if (normalized !== currentClass) {
		el.setAttribute('class', normalized)
	}
}

function normalizeTree(root: ParentNode) {
	if (root instanceof Element) {
		normalizeElementClass(root)
	}

	for (const el of root.querySelectorAll('[class]')) {
		normalizeElementClass(el)
	}
}

export function initYakinikuUtilities(doc?: Document) {
	const resolvedDocument =
		doc ?? (typeof document !== 'undefined' ? document : undefined)
	if (resolvedDocument == null) return () => {}
	resetRuntimeForDocument(resolvedDocument)
	getStyleEl(resolvedDocument)

	normalizeTree(resolvedDocument)

	if (mutationObserver) {
		mutationObserver.disconnect()
	}

	mutationObserver = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			if (mutation.type === 'attributes') {
				const target = mutation.target
				if (
					target instanceof Element &&
					mutation.attributeName === 'class'
				) {
					normalizeElementClass(target)
				}
				continue
			}

			for (const node of mutation.addedNodes) {
				if (!(node instanceof Element)) continue
				normalizeElementClass(node)
				for (const child of node.querySelectorAll('[class]')) {
					normalizeElementClass(child)
				}
			}
		}
	})

	mutationObserver.observe(resolvedDocument.documentElement, {
		attributes: true,
		attributeFilter: ['class'],
		childList: true,
		subtree: true
	})

	return () => {
		mutationObserver?.disconnect()
		mutationObserver = null
	}
}

export { getNormalizedClassString as normalizeClassString }
